import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet } from "lucide-react";

const TEMPLATE_COLS = "company_name,contact_name,title,email,phone,website,cage_code,users_in_scope,lead_source,deal_stage,dod_contracts,asked_about_requirements,handles_cui,ssp_poam,environment,self_assessment,biggest_concern,timeline,who_owns_readiness,decision_maker_access";

export function CsvImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.created} leads`);
      setFile(null);
      onOpenChange(false);
      onImported?.();
    } catch {
      toast.error("Import failed. Check CSV columns.");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_COLS + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "complyx_leads_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy">Import Leads from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Import leads from a Google Sheets export or any CSV. Each row is auto-scored on import.
            Columns are matched by header name.
          </p>
          <button
            data-testid="csv-file-drop"
            onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-border rounded-md p-8 flex flex-col items-center gap-2 hover:border-gold transition-colors"
          >
            <Upload className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm font-medium text-navy">{file ? file.name : "Click to choose a CSV file"}</span>
            <span className="text-xs text-muted-foreground">.csv files only</span>
          </button>
          <input ref={inputRef} type="file" accept=".csv" className="hidden"
            data-testid="csv-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button onClick={downloadTemplate} data-testid="download-template"
            className="inline-flex items-center gap-2 text-xs text-gold-dark hover:underline">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Download CSV template
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="import-submit" onClick={submit} disabled={uploading}
            className="bg-navy text-white hover:bg-navy-700">
            {uploading ? "Importing…" : "Import Leads"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
